'use client';

import { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAppSelector } from '@/store/hooks';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import HeroSection from '@/components/landing/HeroSection';
import FeatureShowcase from '@/components/landing/FeatureShowcase';
import PersonaPreview from '@/components/landing/PersonaPreview';
import { motion } from 'framer-motion';
import {
  Clock,
  Target,
  MessageCircle,
  TrendingUp,
  CalendarCheck,
  FileText,
  ChevronRight,
  ArrowRight,
  Flame,
} from 'lucide-react';
import { usePersona } from '@/shared/hooks/usePersona';
import CoachSpeech from '@/components/ui/CoachSpeech';
import { DASHBOARD_LOGGED_IN } from '@/shared/data/coachingMessages';
import { FEATURE_CARD_COLORS, PERSONA_HERO_STYLES, ANIMATION } from '@/shared/themes/designSystem';
import AmbientBlobs from '@/components/ui/AmbientBlobs';
import StartSheet from '@/components/landing/StartSheet';
import { getLocalStorageItem } from '@/shared/utils/localStorage';
import type { DailyCheckIn } from '@/shared/types/localStorage';
import { getNextRecommendation, type NudgeRecommendation } from '@/shared/services/nudgeService';
import type { MiddayPulse, GrowthMoment } from '@/shared/types/touchpoints';
import { loadGamificationState } from '@/shared/utils/gamification';
import ManagerMessageInbox from '@/components/features/ManagerMessageInbox';

/* ──────────────────────────────────────────────
   로그인 사용자용 대시보드
   ────────────────────────────────────────────── */

/** Compute consecutive check-in days ending today/yesterday */
function computeStreak(checkIns: DailyCheckIn[]): number {
  if (checkIns.length === 0) return 0;
  const dates = [...new Set(checkIns.map((c) => c.date))].sort().reverse();
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const todayStr = fmt(today);
  const yesterdayStr = fmt(new Date(today.getTime() - 86_400_000));

  if (dates[0] !== todayStr && dates[0] !== yesterdayStr) return 0;

  let streak = 1;
  for (let i = 1; i < dates.length; i++) {
    const prev = new Date(dates[i - 1]);
    const cur = new Date(dates[i]);
    const diff = (prev.getTime() - cur.getTime()) / 86_400_000;
    if (Math.abs(diff - 1) < 0.01) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
}

/** Check whether today has a morning and/or evening check-in */
function todayCheckins(checkIns: DailyCheckIn[]): { morning: boolean; evening: boolean } {
  const todayStr = new Date().toISOString().slice(0, 10);
  const todays = checkIns.filter((c) => c.date === todayStr);
  return {
    morning: todays.some((c) => c.type === 'morning'),
    evening: todays.some((c) => c.type === 'evening'),
  };
}

/** Tool color key mapping by href */
const TOOL_COLOR_KEYS: Record<string, string> = {
  '/checkin': 'checkin',
  '/timer': 'timer',
  '/goals': 'goals',
  '/coach': 'coach',
  '/profile/growth': 'growth',
  '/profile/weekly-report': 'report',
};

function Dashboard({ userName }: { userName: string }) {
  const { persona, theme, richProfile } = usePersona();
  const heroStyle = PERSONA_HERO_STYLES[persona];

  // Read check-in data from localStorage
  const checkIns = useMemo(
    () => getLocalStorageItem<DailyCheckIn[]>('daily_checkins', []),
    [],
  );
  const streak = useMemo(() => computeStreak(checkIns), [checkIns]);
  const todayStatus = useMemo(() => todayCheckins(checkIns), [checkIns]);

  // Touchpoint recommendation
  const nudge = useMemo<NudgeRecommendation | null>(() => {
    const timerSessions = getLocalStorageItem<Array<{ startTime?: string; date?: string }>>('timer_sessions', []);
    const pulseRecords = getLocalStorageItem<MiddayPulse[]>('midday_pulses', []);
    const growthMoments = getLocalStorageItem<GrowthMoment[]>('growth_moments', []);
    return getNextRecommendation(checkIns, timerSessions, pulseRecords, growthMoments, persona, richProfile);
  }, [checkIns, persona, richProfile]);

  // Today's growth moment
  const todayGrowthMoment = useMemo(() => {
    const moments = getLocalStorageItem<GrowthMoment[]>('growth_moments', []);
    const todayStr = new Date().toISOString().slice(0, 10);
    return moments.find(m => m.date === todayStr) ?? null;
  }, []);

  // Gamification state
  const gamification = useMemo(() => loadGamificationState(), []);

  const tools = [
    {
      href: '/checkin',
      icon: CalendarCheck,
      label: '오늘의 체크인',
      desc: '오늘 컨디션과 계획을 기록하세요',
    },
    {
      href: '/timer',
      icon: Clock,
      label: '집중 타이머',
      desc: 'AI가 세션을 분석해드려요',
    },
    {
      href: '/goals',
      icon: Target,
      label: '목표 관리',
      desc: 'AI가 실행 단계를 제안해드려요',
    },
    {
      href: '/coach',
      icon: MessageCircle,
      label: 'AI 코치',
      desc: '나에게 맞는 성장 조언 받기',
    },
    {
      href: '/profile/growth',
      icon: TrendingUp,
      label: '성장 추적',
      desc: '나의 변화를 그래프로 확인',
    },
    {
      href: '/profile/weekly-report',
      icon: FileText,
      label: '주간 리포트',
      desc: '이번 주 활동 요약 & AI 분석',
    },
  ];

  const quickLinks = [
    { href: '/onboarding', label: '진단 다시하기' },
    { href: '/my-tests', label: '진단 결과 보기' },
    { href: '/profile', label: '프로필 설정' },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-0 relative" style={{ background: heroStyle.pageBg }}>
      <AmbientBlobs persona={persona} count={2} opacity={0.08} />
      <Navbar />

      <div className="container mx-auto px-5 max-w-lg">
        {/* ─── Coach Welcome ─── */}
        <div className="mt-4 mb-2">
          <CoachSpeech
            message={DASHBOARD_LOGGED_IN[persona]}
            persona={persona}
            variant="header"
            icon="🌟"
          />
        </div>

        {/* ─── Hero Greeting Banner ─── */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="hero-persona rounded-3xl p-7 mt-6 relative overflow-hidden"
          style={{ background: heroStyle.gradient }}
        >
          {/* Decorative light orbs with breathing animation */}
          <div
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20 animate-breathe"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.6), transparent 70%)' }}
          />
          <div
            className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full opacity-15 animate-breathe-slow"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.5), transparent 70%)' }}
          />

          <div className="relative z-10 flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl" role="img" aria-label="mascot">
                  {theme.icons.mascot}
                </span>
                <h1
                  className="text-xl font-bold truncate"
                  style={{ color: heroStyle.heroText }}
                >
                  {userName}님, 반가워요!
                </h1>
              </div>
              <p
                className="text-sm mt-1 leading-relaxed"
                style={{ color: heroStyle.heroSubtext }}
              >
                {theme.tone.greeting}
              </p>
            </div>

            {/* Streak badge on hero */}
            {streak > 0 && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 260, damping: 20 }}
                className="flex flex-col items-center ml-3 flex-shrink-0"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}
                >
                  {streak >= 7 ? (
                    <Flame className="w-5 h-5 text-amber-300" />
                  ) : (
                    <span className="text-lg">
                      {theme.icons.reward}
                    </span>
                  )}
                  <span
                    className="text-sm font-extrabold leading-none mt-0.5"
                    style={{ color: heroStyle.heroText }}
                  >
                    {streak}
                  </span>
                </div>
                <span
                  className="text-[10px] font-medium mt-1"
                  style={{ color: heroStyle.heroSubtext }}
                >
                  연속
                </span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* ─── Quick Stat Cards ─── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="grid grid-cols-2 gap-3.5 mt-6"
        >
          {/* Streak stat card */}
          <div
            className={`stat-card bg-gradient-to-br ${heroStyle.statGradient}`}
          >
            <span className="text-2xl font-extrabold">{streak}</span>
            <span className="text-[11px] font-medium opacity-90">
              {streak >= 7 ? 'Day Streak' : '일 연속'}
            </span>
          </div>

          {/* Today check-in status */}
          <div
            className={`stat-card bg-gradient-to-br ${heroStyle.statGradientAlt}`}
          >
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-center gap-0.5">
                <div
                  className="w-3 h-3 rounded-full border-2 border-white/60"
                  style={{
                    background: todayStatus.morning ? '#ffffff' : 'rgba(255,255,255,0.25)',
                  }}
                />
                <span className="text-[9px] opacity-75">AM</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <div
                  className="w-3 h-3 rounded-full border-2 border-white/60"
                  style={{
                    background: todayStatus.evening ? '#ffffff' : 'rgba(255,255,255,0.25)',
                  }}
                />
                <span className="text-[9px] opacity-75">PM</span>
              </div>
            </div>
            <span className="text-[11px] font-medium opacity-90 mt-1">
              오늘 체크인
            </span>
          </div>
        </motion.div>

        {/* ─── XP / Level Mini Card ─── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.4 }}
          className="mt-4"
        >
          <Link
            href="/achievements"
            className="block bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all active:scale-[0.98] group"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 font-bold"
                style={{ background: theme.colors.primaryLight, color: theme.colors.primaryDark }}
              >
                {gamification.level.level}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-gray-800">Lv.{gamification.level.level}</span>
                  <span className="text-xs text-gray-400">{gamification.level.totalXP} XP</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: `${gamification.level.requiredXP > 0 ? Math.round((gamification.level.currentXP / gamification.level.requiredXP) * 100) : 0}%`,
                      background: theme.colors.primary,
                    }}
                  />
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
            </div>
          </Link>
        </motion.div>

        {/* ─── Touchpoint Nudge ─── */}
        {nudge && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="mt-6"
          >
            <Link
              href={nudge.touchpoint.route}
              className="block bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all active:scale-[0.98] group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 group-hover:scale-110 transition-transform" style={{ backgroundColor: theme.colors.primaryLight }}>
                  {nudge.touchpoint.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-bold text-gray-800 truncate">{nudge.touchpoint.nameKo}</p>
                    {nudge.priority === 'high' && (
                      <span className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: theme.colors.primary, backgroundColor: theme.colors.primaryLight }}>추천</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{nudge.message}</p>
                  {nudge.reason !== '현재 시간대에 맞는 활동이에요.' && (
                    <p className="text-[10px] mt-0.5" style={{ color: theme.colors.primary }}>{nudge.reason}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            </Link>
          </motion.div>
        )}

        {/* ─── Today's Growth Moment ─── */}
        {todayGrowthMoment && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="mt-4"
          >
            <Link
              href="/growth-moment"
              className="block bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-xl flex-shrink-0">
                  💡
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-emerald-600 mb-0.5">오늘의 성찰</p>
                  <p className="text-sm text-gray-700 line-clamp-2">{todayGrowthMoment.response}</p>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* ─── Feature Tool Grid ─── */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={ANIMATION.stagger}
          className="grid grid-cols-2 gap-4 mt-8"
        >
          {tools.map((tool) => {
            const colorKey = TOOL_COLOR_KEYS[tool.href] ?? 'checkin';
            const colors = FEATURE_CARD_COLORS[colorKey];
            const Icon = tool.icon;

            return (
              <motion.div
                key={tool.href}
                variants={ANIMATION.fadeUp}
                whileHover={ANIMATION.hoverLift}
                whileTap={ANIMATION.tapScale}
              >
                <Link
                  href={tool.href}
                  className="group block rounded-2xl p-5 transition-all duration-200 hover:shadow-lg active:scale-[0.97] h-full"
                  style={{
                    backgroundColor: colors.bg,
                    boxShadow: `0 1px 4px ${colors.shadowColor}`,
                  }}
                >
                  {/* Icon circle */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto transition-transform duration-200 group-hover:scale-110"
                    style={{ backgroundColor: colors.iconBg }}
                  >
                    <Icon className="w-5 h-5" style={{ color: colors.iconColor }} />
                  </div>

                  {/* Label */}
                  <h3
                    className="text-sm font-bold text-center mb-1"
                    style={{ color: colors.textAccent }}
                  >
                    {tool.label}
                  </h3>

                  {/* Description */}
                  <p className="text-[11px] text-center leading-relaxed line-clamp-2" style={{ color: '#8a8078' }}>
                    {tool.desc}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Quick Links (Pill Buttons) ─── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 0.3 }}
          className="mt-6 mb-8 flex flex-wrap gap-2 justify-center"
        >
          {quickLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 transition-all shadow-sm"
            >
              {link.label}
              <ChevronRight className="w-3 h-3" />
            </Link>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   비로그인 사용자용 랜딩
   ────────────────────────────────────────────── */
function Landing() {
  const [showStartSheet, setShowStartSheet] = useState(false);

  const handleStartClick = () => setShowStartSheet(true);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero with animated gradient blobs */}
      <HeroSection onStartClick={handleStartClick} />

      {/* Interactive feature cards with mini previews */}
      <FeatureShowcase />

      {/* Persona preview with live switching */}
      <PersonaPreview />

      {/* CTA — Coach inviting you */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden"
      >
        <div className="py-28 sm:py-36" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)' }}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="relative container mx-auto px-4 max-w-2xl text-center">
            {/* Coach avatar */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 20 }}
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-2xl font-black text-white"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.25)' }}
            >
              AI
            </motion.div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 leading-snug">
              여기까지 읽어주셨군요.
              <br />
              <span className="text-white/80">그 관심이 이미 성장의 시작이에요.</span>
            </h2>
            <p className="text-white/60 mb-10 text-lg leading-relaxed">
              1분이면 나에게 맞는 코치를 만날 수 있어요.
              <br />
              준비되셨으면, 같이 시작해볼까요?
            </p>
            <button
              onClick={handleStartClick}
              className="group inline-flex items-center justify-center gap-2.5 px-10 py-5 bg-white text-lg font-bold rounded-2xl hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0"
              style={{ color: '#4338ca' }}
            >
              같이 시작하기
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <Footer />

      {/* Start sheet — new/returning user choice */}
      <StartSheet open={showStartSheet} onClose={() => setShowStartSheet(false)} />
    </div>
  );
}

/* ──────────────────────────────────────────────
   메인 라우트
   ────────────────────────────────────────────── */
export default function HomePage() {
  const router = useRouter();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // 부모/관리자는 전용 페이지로 리다이렉트
  useEffect(() => {
    if (!mounted || !isAuthenticated || !user) return;
    if (user.account_type === 'parent') router.replace('/parent/children');
    if (user.account_type === 'teacher') router.replace('/group-manager');
  }, [mounted, isAuthenticated, user, router]);

  if (!mounted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Dashboard userName={user.full_name || user.username || ''} />;
  }

  return <Landing />;
}
